import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, AlertTriangle, ListTodo, UserCheck, Activity, CalendarClock, ArrowUpRight, CalendarDays, Megaphone, PencilLine, CalendarHeart } from "lucide-react";
import { ChannelIcons, PostStatoBadge } from "@/components/ped/PedBits";
import { formatDistanceToNow, format } from "date-fns";
import { it } from "date-fns/locale";
import { tipoOf } from "@/lib/calendar";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/hooks";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHeader, Panel, EmptyState, FullLoader } from "@/components/core/AppLayout";
import { ActivityLogButton } from "@/components/core/ActivityLog";
import { EconomicDashboard } from "@/components/invoices/EconomicDashboard";
import { OnboardingCard } from "@/components/core/Onboarding";
import { CompetitorNewsWidget } from "@/components/adminai/CompetitorNewsWidget";
import { PriorityBadge, StatusSelect } from "@/components/core/TaskBadges";

const Kpi = ({ icon: Icon, label, value, accent, sky, testid, delay }) => (
  <Panel className={`p-6 core-rise ${delay} ${accent ? "bg-[#0A1128] border-[#0A1128]" : sky ? "bg-[#6BBAF0] border-[#5FB3EA]" : ""}`} data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className={`text-xs font-bold uppercase tracking-wider ${accent ? "text-white/60" : sky ? "text-[#0A1128]/70" : "text-slate-500"}`}>{label}</div>
      <Icon className={`h-5 w-5 ${accent ? "text-[#FF5A72]" : sky ? "text-[#E8173A]" : "text-[#0A1128]"}`} />
    </div>
    <div className={`font-display text-4xl sm:text-5xl font-black mt-3 sm:mt-4 ${accent ? "text-white" : "text-[#0A1128]"}`} data-testid={`${testid}-value`}>{value}</div>
  </Panel>
);

const TaskLine = ({ t, onStatus }) => (
  <div className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0" data-testid={`dash-task-${t.id}`}>
    <div className={`h-9 w-1 rounded-full ${t.scaduta ? "bg-[#E8173A]" : "bg-[#0A1128]/20"}`} />
    <div className="flex-1 min-w-0">
      <div className="font-bold text-sm text-slate-900 truncate">{t.titolo}</div>
      <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
        <Link to={`/clienti/${t.client_id}`} className="font-semibold text-[#0A1128] hover:text-[#E8173A]">{t.client_nome}</Link>
        <span>·</span>
        <span className={`inline-flex items-center gap-1 ${t.scaduta ? "text-[#E8173A] font-bold" : ""}`}><CalendarClock className="h-3 w-3" />{fmtDate(t.scadenza)}</span>
        {t.assignee_nome && <><span>·</span><span>{t.assignee_nome}</span></>}
      </div>
    </div>
    <PriorityBadge value={t.priorita} />
    <StatusSelect task={t} onChange={onStatus} />
  </div>
);

const TaskPanel = ({ title, icon: Icon, tasks, onStatus, empty, testid, link }) => (
  <Panel className="p-6" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <h3 className="font-display text-lg font-extrabold text-[#0A1128] flex items-center gap-2"><Icon className="h-5 w-5 text-[#E8173A]" />{title}</h3>
      {link && <Link to={link} className="text-xs font-bold text-[#0A1128] hover:text-[#E8173A] inline-flex items-center gap-1" data-testid={`${testid}-link`}>Vedi tutte <ArrowUpRight className="h-3.5 w-3.5" /></Link>}
    </div>
    {tasks.length ? tasks.slice(0, 8).map((t) => <TaskLine key={t.id} t={t} onStatus={onStatus} />) : <EmptyState icon={Icon} title={empty} testid={`${testid}-empty`} />}
  </Panel>
);

const WeekPosts = ({ items }) => (
  <Panel className="p-6" data-testid="dash-ped-week">
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-display text-lg font-extrabold text-[#0A1128] flex items-center gap-2"><Megaphone className="h-5 w-5 text-[#E8173A]" />Post PED della settimana</h3>
      <span className="text-xs font-bold text-slate-400">{items.length} post</span>
    </div>
    {items.length ? items.map((p) => (
      <Link key={p.id} to={`/clienti/${p.client_id}?tab=ped&post=${p.id}`} className="flex items-center gap-4 py-2.5 border-b border-slate-100 last:border-0 hover:bg-slate-50 -mx-2 px-2 rounded-lg" data-testid={`dash-post-${p.id}`}>
        <div className="w-24 shrink-0 text-xs font-bold text-slate-500 capitalize">{format(new Date(p.data_pubblicazione), "EEE d · HH:mm", { locale: it })}</div>
        <ChannelIcons canali={p.canali} size={15} />
        <div className="min-w-0 flex-1">
          <div className="font-bold text-sm text-slate-900 truncate">{p.titolo}</div>
          <div className="text-xs text-slate-500 truncate">{p.client_nome}</div>
        </div>
        <PostStatoBadge stato={p.stato} />
      </Link>
    )) : <EmptyState icon={Megaphone} title="Nessun post programmato questa settimana" testid="dash-ped-week-empty" />}
  </Panel>
);

const MyEvents = () => {
  const { data } = useFetch("/me/eventi/prossimi?limit=5");
  return (
    <Panel className="p-6" data-testid="dash-my-events">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-extrabold text-[#0A1128] flex items-center gap-2"><CalendarHeart className="h-5 w-5 text-[#E8173A]" />I miei prossimi eventi personali</h3>
        <Link to="/il-mio-calendario" className="text-xs font-bold text-[#0A1128] hover:text-[#E8173A] inline-flex items-center gap-1" data-testid="dash-my-events-link">Apri <ArrowUpRight className="h-3.5 w-3.5" /></Link>
      </div>
      {data && !data.length && <div className="text-sm text-slate-500" data-testid="dash-my-events-empty">Nessun evento personale in programma.</div>}
      {(data || []).map((e) => (
        <Link key={e.id} to={`/il-mio-calendario?evento=${e.id}`} className="flex items-center gap-3 rounded-xl p-2 -mx-2 hover:bg-[#A6DAFC] transition-colors" data-testid={`dash-my-event-${e.id}`}>
          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: e.colore }} />
          <span className="font-bold text-sm text-slate-900 truncate flex-1">{e.titolo}</span>
          <span className="text-xs text-slate-500 shrink-0 capitalize">{format(new Date(e.inizio), e.all_day ? "EEE d MMM" : "EEE d MMM, HH:mm", { locale: it })}</span>
        </Link>
      ))}
    </Panel>
  );
};

const ChangesPosts = ({ items }) => (
  <Panel className="p-6" data-testid="dash-ped-changes">
    <h3 className="font-display text-lg font-extrabold text-[#0A1128] flex items-center gap-2 mb-4">
      <PencilLine className="h-5 w-5 text-[#E8173A]" />Post con modifiche richieste
      <span className="ml-auto rounded-full bg-[#E8173A] text-white text-xs font-bold px-2.5 py-0.5" data-testid="dash-ped-changes-count">{items.length}</span>
    </h3>
    {items.length ? items.map((p) => (
      <Link key={p.id} to={`/clienti/${p.client_id}?tab=ped&post=${p.id}`} className="flex items-center gap-3 rounded-xl p-2 -mx-2 hover:bg-[#A6DAFC] transition-colors" data-testid={`dash-ped-changes-${p.id}`}>
        <span className="h-2.5 w-2.5 rounded-full bg-[#E8173A] shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-bold text-sm text-slate-900 truncate">{p.titolo}</div>
          <div className="text-xs text-slate-500 truncate">{p.client_nome} · {format(new Date(p.data_pubblicazione), "d MMM, HH:mm", { locale: it })}</div>
        </div>
      </Link>
    )) : <div className="text-sm text-slate-500" data-testid="dash-ped-changes-empty">Nessuna richiesta di modifica in sospeso.</div>}
  </Panel>
);

const UpcomingAppointments = ({ items }) => (
  <Panel className="p-6" data-testid="dash-appointments">
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-display text-lg font-extrabold text-[#0A1128] flex items-center gap-2"><CalendarDays className="h-5 w-5 text-[#E8173A]" />Prossimi appuntamenti</h3>
      <Link to="/calendario" className="text-xs font-bold text-[#0A1128] hover:text-[#E8173A] inline-flex items-center gap-1" data-testid="dash-appointments-link">Calendario <ArrowUpRight className="h-3.5 w-3.5" /></Link>
    </div>
    {items.length ? (
      <div className="space-y-3">
        {items.map((a) => {
          const d = new Date(a.inizio);
          return (
            <Link key={a.id} to={`/clienti/${a.client_id}`} className="flex items-center gap-4 rounded-xl p-2 -mx-2 hover:bg-slate-50 transition-colors" data-testid={`dash-appt-${a.id}`}>
              <div className="w-12 shrink-0 rounded-xl border border-slate-200 text-center py-1.5">
                <div className="text-[10px] font-bold uppercase text-[#E8173A]">{format(d, "MMM", { locale: it })}</div>
                <div className="font-display text-xl font-black text-[#0A1128] leading-none">{format(d, "d")}</div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm text-slate-900 truncate">{a.titolo}</div>
                <div className="text-xs text-slate-500 truncate">
                  <span className="capitalize">{format(d, "EEE HH:mm", { locale: it })}</span> · {a.client_nome}
                </div>
              </div>
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: tipoOf(a.tipo).color }} title={tipoOf(a.tipo).label} />
            </Link>
          );
        })}
      </div>
    ) : <EmptyState icon={CalendarDays} title="Nessun appuntamento in programma" testid="dash-appointments-empty" />}
  </Panel>
);

export default function DashboardPage() {
  const { user } = useAuth();
  const { data, reload } = useFetch("/dashboard");
  if (!data) return <FullLoader />;

  const onStatus = async (t, stato) => {
    try {
      await api.patch(`/tasks/${t.id}/status`, { stato });
      toast.success("Stato aggiornato");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const urgent = [...data.task_scadute, ...data.task_in_scadenza];

  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow="Panoramica" title={`Ciao, ${user.nome}`} subtitle="Ecco cosa succede oggi in agenzia." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mb-8 core-m2">
        <Kpi icon={Building2} label="Clienti attivi" value={data.clienti_attivi} testid="kpi-clienti-attivi" accent delay="core-delay-1" />
        <Kpi icon={ListTodo} label="Task aperte" value={data.task_aperte} testid="kpi-task-aperte" sky delay="core-delay-2" />
        <Kpi icon={AlertTriangle} label="Task scadute" value={data.task_scadute_count ?? data.task_scadute.length} testid="kpi-task-scadute" delay="core-delay-3" />
        <Kpi icon={UserCheck} label="Assegnate a me" value={data.task_mie_count ?? data.task_mie.length} testid="kpi-task-mie" delay="core-delay-4" />
      </div>
      <OnboardingCard />
      {user.role === "admin" && <CompetitorNewsWidget />}
      <EconomicDashboard />
      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-6">
        <div className="space-y-6">
          <TaskPanel title="Scadute e in scadenza (7 giorni)" icon={AlertTriangle} tasks={urgent} onStatus={onStatus} empty="Nessuna scadenza imminente" testid="dash-urgent" link="/task" />
          <TaskPanel title="Le mie task" icon={UserCheck} tasks={data.task_mie} onStatus={onStatus} empty="Nessuna task assegnata a te" testid="dash-mine" link="/task?mine=1" />
          <WeekPosts items={data.post_settimana || []} />
        </div>
        <div className="space-y-6">
        <MyEvents />
        <ChangesPosts items={data.post_modifiche || []} />
        <UpcomingAppointments items={data.prossimi_appuntamenti || []} />
        <Panel className="p-6 h-fit" data-testid="dash-activity">
          <h3 className="font-display text-lg font-extrabold text-[#0A1128] flex items-center gap-2 mb-4"><Activity className="h-5 w-5 text-[#E8173A]" />Attività recenti</h3>
          <div className="text-xs text-slate-500 mb-4">
            {data.clienti_totali} clienti totali · {data.clienti_archiviati} archiviati
          </div>
          {data.attivita.length ? (
            <ol className="relative border-l-2 border-slate-100 ml-1.5 space-y-5">
              {data.attivita.map((a) => (
                <li key={a.id} className="pl-5 relative" data-testid={`activity-${a.id}`}>
                  <span className="absolute -left-[7px] top-1 h-3 w-3 rounded-full bg-[#A6DAFC] border-2 border-[#E8173A]" />
                  <div className="text-sm text-slate-700 leading-snug">
                    <span className="font-bold text-slate-900">{a.user_nome}</span> {a.azione}{" "}
                    {a.client_id && a.target_tipo === "cliente" ? <Link to={`/clienti/${a.client_id}`} className="font-bold text-[#0A1128] hover:text-[#E8173A]">{a.target_nome}</Link> : <span className="font-semibold">{a.target_nome}</span>}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{formatDistanceToNow(new Date(a.created_at), { addSuffix: true, locale: it })}</div>
                </li>
              ))}
            </ol>
          ) : <EmptyState icon={Activity} title="Nessuna attività" />}
          <ActivityLogButton />
        </Panel>
        </div>
      </div>
    </div>
  );
}
